import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Link, useSearchParams } from 'react-router-dom';
import { UNSUBSCRIBE_BY_TOKEN, type UnsubscribeByTokenData, type UnsubscribeByTokenVars } from '../../graphql/mutations/unsubscribe';
import { useBrand } from '../brand';
import { useViewer } from '../shell';
import './unsubscribe.css';

type Outcome = 'done' | 'invalid' | 'error' | null;

/**
 * The page an email's unsubscribe link opens (/unsubscribe?token=...). The token
 * identifies the customer, so no sign-in is needed. Nothing happens until the
 * customer presses the button: mail scanners open links, and must not unsubscribe anyone.
 */
export function UnsubscribeView() {
  const brand = useBrand();
  const viewer = useViewer();
  const [params] = useSearchParams();
  const token = params.get('token')?.trim() ?? '';
  const [everything, setEverything] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>(null);
  const [unsubscribe, { loading }] = useMutation<UnsubscribeByTokenData, UnsubscribeByTokenVars>(UNSUBSCRIBE_BY_TOKEN);

  const submit = async () => {
    setOutcome(null);
    try {
      const { data } = await unsubscribe({ variables: { token, everything } });
      setOutcome(data?.unsubscribeByToken ? 'done' : 'invalid');
    } catch {
      setOutcome('error');
    }
  };

  const prefsLink = (
    <p className="ui-unsub-note">
      {viewer.isAuthenticated ? 'Changed your mind? ' : 'Changed your mind? Sign in and '}
      <Link to="/profile">{viewer.isAuthenticated ? 'Manage your email preferences' : 'manage your email preferences'}</Link>
      {' on your profile.'}
    </p>
  );

  if (!token || outcome === 'invalid') {
    return (
      <section className="ui-unsub" aria-labelledby="ui-unsub-title">
        <h1 id="ui-unsub-title" className="ui-unsub-title">This unsubscribe link isn't valid or has expired</h1>
        <p className="ui-unsub-text">
          You can still choose which emails you get from {brand.name} on your profile.
        </p>
        <div className="ui-unsub-actions">
          <Link className="ui-unsub-btn primary" to="/profile">Manage email preferences</Link>
          <Link className="ui-unsub-btn" to="/contact">Contact us</Link>
        </div>
      </section>
    );
  }

  if (outcome === 'done') {
    return (
      <section className="ui-unsub" aria-labelledby="ui-unsub-title">
        <h1 id="ui-unsub-title" className="ui-unsub-title">You're unsubscribed</h1>
        <p className="ui-unsub-text" role="status">
          {everything
            ? "You won't get offers, marketing or tips emails from us any more."
            : "You won't get offers or marketing emails from us any more."}
        </p>
        {prefsLink}
      </section>
    );
  }

  return (
    <section className="ui-unsub" aria-labelledby="ui-unsub-title">
      <p className="ui-unsub-site">{brand.name}</p>
      <h1 id="ui-unsub-title" className="ui-unsub-title">Unsubscribe</h1>
      <p className="ui-unsub-text">Unsubscribe from offers and marketing emails from {brand.name}?</p>
      <label className="ui-unsub-check">
        <input type="checkbox" checked={everything} onChange={(e) => setEverything(e.target.checked)} disabled={loading} />
        <span>Also stop tips emails (the weekly and midweek tips)</span>
      </label>
      {outcome === 'error' && (
        <div className="ui-unsub-error" role="alert">
          Something went wrong and you haven't been unsubscribed yet. Please try again.
        </div>
      )}
      <div className="ui-unsub-actions">
        <button type="button" className="ui-unsub-btn primary" onClick={submit} disabled={loading}>
          {loading ? 'Unsubscribing…' : outcome === 'error' ? 'Try again' : 'Unsubscribe'}
        </button>
      </div>
    </section>
  );
}
